import { supabase } from '../lib/supabase';

// CRITICAL FIX: Get effective user ID for all queries
const getEffectiveUserId = () => {
  const impersonationData = localStorage.getItem('admin_impersonation');
  if (impersonationData) {
    const { impersonatedUserId } = JSON.parse(impersonationData);
    return impersonatedUserId;
  }
  return null;
};

// Add this helper function
const toCamelCaseArray = (data) => {
  if (!data) return [];
  return data?.map(item => ({
    id: item?.id,
    userId: item?.user_id,
    revenue: item?.revenue,
    costOfGoodsSold: item?.cost_of_goods_sold,
    cogsPercentage: item?.cogs_percentage,
    grossProfit: item?.gross_profit,
    grossMarginPercentage: item?.gross_margin_percentage,
    calculationPeriod: item?.calculation_period,
    periodStartDate: item?.period_start_date,
    periodEndDate: item?.period_end_date,
    createdAt: item?.created_at,
    updatedAt: item?.updated_at,
  }));
};

export const cogsCalculatorService = {
  /**
   * Get all COGS calculations for current user
   * @returns {Promise<Array>}
   */
  async getCalculations() {
    try {
      const effectiveUserId = getEffectiveUserId();
      
      let query = supabase
        ?.from('cogs_calculations')
        ?.select('*')
        ?.order('created_at', { ascending: false });
      
      if (effectiveUserId) {
        query = query?.eq('user_id', effectiveUserId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return toCamelCaseArray(data);
    } catch (error) {
      console.error('Error fetching COGS calculations:', error);
      throw error;
    }
  },

  /**
   * Alias for getCalculations - for backwards compatibility
   * @returns {Promise<Array>}
   */
  async getAll() {
    return this.getCalculations();
  },

  /**
   * Create a new COGS calculation
   */
  async create(calculation) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('cogs_calculations')?.insert({
        user_id: user?.id,
        revenue: calculation?.revenue,
        cost_of_goods_sold: calculation?.costOfGoodsSold,
        cogs_percentage: calculation?.cogsPercentage,
        gross_profit: calculation?.grossProfit,
        gross_margin_percentage: calculation?.grossMarginPercentage,
        calculation_period: calculation?.calculationPeriod,
        period_start_date: calculation?.periodStartDate,
        period_end_date: calculation?.periodEndDate,
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      revenue: data?.revenue,
      costOfGoodsSold: data?.cost_of_goods_sold,
      cogsPercentage: data?.cogs_percentage,
      grossProfit: data?.gross_profit,
      grossMarginPercentage: data?.gross_margin_percentage,
      calculationPeriod: data?.calculation_period,
      periodStartDate: data?.period_start_date,
      periodEndDate: data?.period_end_date,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
    };
  },

  /**
   * Delete a calculation
   */
  async delete(id) {
    const { error } = await supabase?.from('cogs_calculations')?.delete()?.eq('id', id);

    if (error) throw error;
  },

  /**
   * Delete all calculations for the authenticated user
   */
  async deleteAll() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('cogs_calculations')?.delete()?.eq('user_id', user?.id);

    if (error) throw error;
  },
};